import dayjs from 'dayjs';
import {
  ChartTitleProps,
  YAxisProps,
  XAxisProps,
  YAxisLabelStyles,
  XAxisLabelStyles,
} from '../../../components/highchartUtils';
import { BrandDarkBlue, GreenColor } from '../../../theme';
import { GraphData } from '../../../Types/types';
import { calculatedKwhSensor } from '../context/ChartContext';

function ceilToNearestDecimal(calculated: number): number {
  if (calculated < 1) {
    // Find the first non-zero digit after the decimal point
    const magnitude = Math.pow(10, Math.ceil(-Math.log10(calculated)));
    return Math.ceil(calculated * magnitude) / magnitude;
  } else {
    // Standard Math.ceil for numbers >= 1
    return Math.ceil(calculated);
  }
}

function calcMinimumY(graphData: GraphData[]) {
  if (graphData.length === 0) return 0;
  const min = graphData.reduce((prev, curr) => (prev[1] < curr[1] ? prev : curr))[1];
  const calculated = min * 0.9999;
  return Math.floor(calculated);
}

function calcMaximumY(graphData: GraphData[]) {
  if (graphData.length === 0) return 100;
  const max = graphData.reduce((prev, curr) => (prev[1] > curr[1] ? prev : curr))[1];
  const calculated = Math.max(max * 1.0001, 0.1); // if max is 0 then the y-axis is from 0 to 0 which doesn not make sense

  return ceilToNearestDecimal(calculated);
}

export default function getDefaultGraphSettings(showDataPoints: boolean, graphData: GraphData[], sensor: string) {
  return {
    time: {
      getTimezoneOffset: function (timestamp: string) {
        return new Date(timestamp).getTimezoneOffset();
      },
    },
    chart: {
      backgroundColor: BrandDarkBlue,
      zoomType: 'x',
      spacing: [30, 10, 10, 10],
    },
    boost: {
      useGPUTranslations: true,
    },
    title: {
      ...ChartTitleProps,
      text: sensor === calculatedKwhSensor ? 'kWh' : sensor,
      style: {
        color: 'white',
        fontFamily: 'DM Sans',
      },
      margin: 0,
      y: 0,
      verticalAlign: 'top',
    },
    legend: {
      enabled: false,
    },
    yAxis: [
      {
        ...YAxisProps, // First y-axis (primary)
        labels: {
          ...YAxisLabelStyles,
        },

        min: calcMinimumY(graphData), // this scales the y-axis to show small changes better
        max: calcMaximumY(graphData), // this scales the y-axis to show small changes better
      },
    ],

    xAxis: {
      ...XAxisProps,
      type: 'datetime',
      labels: {
        y: 25,
        ...XAxisLabelStyles,
      },
    },
    plotOptions: {
      series: {
        lineWidth: 3,
      },
      area: {
        marker: {
          enabled: false,
          symbol: 'circle',
          radius: 3,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
      },
    },
    tooltip: {
      enabled: true,
      backgroundColor: '#ffffff',
      useHTML: true,
      formatter: function (this: Highcharts.Point) {
        const date = dayjs(new Date(this.x));
        const newDate = date.format('dddd, MMMM D, HH:mm:ss');

        const toFixedIfNecessary = (value: number, precision: number) => {
          return value.toFixed(precision);
        };

        return `<div style="margin: 0;"><p style="font-size: 11px; margin: 0;">${newDate}<p>
										<p style="color:${GreenColor}; margin: 0; display:inline-block;">●</p>
										<p style="font-size: 13px; margin: 0; display:inline-block;">Value: <b>${toFixedIfNecessary(this.y ?? 0, 2)}</b></p>
								</div>`;
      },
    },

    series: [
      {
        fillColor: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0.45, 'rgba(39, 206, 126, 0.45)'], // start
            [1, 'rgba(39, 206, 126, 0)'], // end
          ],
        },
        type: sensor === calculatedKwhSensor ? 'column' : 'area',
        name: 'Device data',
        color: GreenColor,
        dashStyle: 'solid',
        data: graphData,
        marker: {
          enabled: showDataPoints,
          symbol: 'circle',
          radius: 3,
          lineWidth: 1,
          lineColor: '#ffffffbb',
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        yAxis: 0,
      },
    ],
  };
}
